import { CheckCircle, PieChart, Clock } from "lucide-react";
import { MetricCard } from "./MetricCard";
import { ActivitySection } from "./ActivitySection";
import { useAuth } from "@/contexts/AuthContext";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Loader2 } from "lucide-react";
import { ProfileNotification } from "@/components/shared/ProfileNotification";
import { CalendlyNotification } from "@/components/shared/CalendlyNotification";
import { InterviewAvailabilityNotification } from "@/components/shared/InterviewAvailabilityNotification";
import { usePendingInterviews } from "@/hooks/usePendingInterviews";
import { InProgressTestsSection } from "./InProgressTestsSection";
import { ActiveTestsBanner } from "@/components/shared/ActiveTestsBanner";

export function CandidateDashboard() {
  const { user } = useAuth();
  
  // Fetch pending interview invitations
  const { data: pendingInvitations = [] } = usePendingInterviews();

  // Fetch in-progress tests
  const { data: inProgressTests = [] } = useQuery({
    queryKey: ['in-progress-tests', user?.id],
    queryFn: async () => {
      if (!user?.id) return [];
      
      const { data, error } = await supabase
        .from('test_responses')
        .select(`
          id,
          test_id,
          started_at,
          last_question_index,
          tests (
            id,
            title,
            questions (count)
          )
        `)
        .eq('candidate_id', user.id)
        .eq('status', 'in_progress')
        .order('started_at', { ascending: false });

      if (error) throw error;
      return data || [];
    },
    enabled: !!user?.id,
  });

  const { data: profile } = useQuery({
    queryKey: ['profile', user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      const { data, error } = await supabase
        .from('profiles')
        .select('full_name, job_title, location, calendly_url')
        .eq('id', user.id)
        .single();
      
      if (error) throw error;
      return data;
    },
    enabled: !!user?.id,
  });

  const isProfileComplete = Boolean(
    profile?.job_title && 
    profile?.location
  );

  const { data: metrics, isLoading } = useQuery({
    queryKey: ['candidate-metrics', user?.id],
    queryFn: async () => {
      if (!user?.id) return null;

      const [testResponses, testAssignments] = await Promise.all([
        supabase
          .from('test_responses')
          .select('*')
          .eq('candidate_id', user.id),
        supabase
          .from('test_assignments')
          .select('*')
          .eq('candidate_id', user.id)
          .eq('status', 'pending')
      ]);

      if (testResponses.error) throw testResponses.error;
      if (testAssignments.error) throw testAssignments.error;

      const completedTests = testResponses.data.filter(r => r.status === 'completed').length;
      const inProgressTests = testResponses.data.filter(r => r.status === 'in_progress').length;
      const avgScore = testResponses.data.length > 0
        ? Math.round(testResponses.data.reduce((acc, curr) => acc + (curr.score || 0), 0) / testResponses.data.length)
        : 0;

      return {
        completedTests,
        inProgressTests,
        averageScore: avgScore,
        upcomingTests: testAssignments.data
      };
    },
    enabled: !!user?.id
  });

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-[200px]">
        <Loader2 className="h-8 w-8 animate-spin text-foreground" />
      </div>
    );
  }

  const hour = new Date().getHours();
  let greeting = "Good Evening";
  
  if (hour < 12) {
    greeting = "Good Morning";
  } else if (hour < 18) {
    greeting = "Good Afternoon";
  }

  // Extract first name or use full name
  const firstName = profile?.full_name?.split(' ')[0] || 'User';

  return (
    <div className="min-h-screen w-full bg-background pb-20 md:pb-0">
      <ActiveTestsBanner />
      
      <div className="container max-w-7xl mx-auto py-6 px-6 md:px-12 lg:px-24 space-y-6 md:space-y-8">
        <h1 className="text-3xl font-bold text-foreground">
          {greeting}, {firstName} 👋
        </h1>
        
        {profile && (
          <>
            <ProfileNotification 
              userName={firstName}
              role="candidate"
              isProfileComplete={isProfileComplete}
            />
            {isProfileComplete && (
              <CalendlyNotification 
                userName={firstName}
                hasCalendlyUrl={Boolean(profile.calendly_url)}
              />
            )}
            <InterviewAvailabilityNotification 
              pendingInvitations={pendingInvitations}
            />
          </>
        )}
        
        <InProgressTestsSection tests={inProgressTests} />
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <MetricCard
            title="Tests Completed"
            value={metrics?.completedTests || 0}
            icon={CheckCircle}
            description="Total number of completed tests"
          />
          <MetricCard
            title="Tests In Progress"
            value={metrics?.inProgressTests || 0}
            icon={Clock}
            description="Tests you can resume"
          />
          <MetricCard
            title="Avg. Score"
            value={`${metrics?.averageScore || 0}%`}
            icon={PieChart}
            description="Average score across all tests"
          />
        </div>
        
        <ActivitySection title="Upcoming Tests">
          {metrics?.upcomingTests && metrics.upcomingTests.length > 0 ? (
            <div className="space-y-4">
              {metrics.upcomingTests.map((test) => (
                <div key={test.id} className="p-4 border rounded-sm bg-card text-card-foreground">
                  <h3 className="font-medium">Test Assignment</h3>
                  <p className="text-sm text-muted-foreground">
                    Assigned on: {new Date(test.created_at).toLocaleDateString()}
                  </p>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-muted-foreground">No upcoming tests</p>
          )}
        </ActivitySection>
      </div>
    </div>
  );
}
